/**
 * resonancePipeline — two coupled pendulums, rendered as both
 * physical motion AND phase-space trajectory.
 *
 * Two masses connected by a light spring, each also connected to a
 * fixed wall by its own spring. The motion decomposes into two normal
 * modes:
 *   - In-phase: both masses swing together (low frequency)
 *   - Out-of-phase: masses swing in opposition (high frequency)
 * When you start the system at an asymmetric position, energy
 * oscillates between the two modes at the BEAT frequency.
 *
 * Innovations:
 *   - Phase-space plot in the corner (x1 vs x2 trajectory) — the
 *     iconic "Lissajous-by-energy-transfer" figure that emerges
 *     from coupled oscillators.
 *   - Energy density radius — the stroked orbit pulses when the
 *     energy concentrates in one mode.
 *   - The two masses are drawn as dropped highlights along a
 *     horizontal "track" so the eye can read the motion directly.
 *   - Spring connecting the masses is drawn as a stretched coil
 *     (helical) whose tightness encodes the spring constant.
 *
 * Audio:
 *   - bass: beat frequency (energy swap rate)
 *   - mid:  damping (slower decay)
 *   - high: spring tension (visible coil tightness)
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface ResonancePipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

// Two normal modes of a coupled-pendulum system:
//   - ω1 (in-phase):  both masses swing together (lower freq)
//   - ω2 (out-of-phase): masses swing oppositely (higher freq)
// The exact motion is x1(t) = A1·cos(ω1·t) + A2·cos(ω2·t)
//                      x2(t) = A1·cos(ω1·t) - A2·cos(ω2·t)
vec2 displacement(float t, float w1, float w2, float A1, float A2) {
  float x1 = A1 * cos(w1 * t) + A2 * cos(w2 * t);
  float x2 = A1 * cos(w1 * t) - A2 * cos(w2 * t);
  return vec2(x1, x2);
}

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / uResolution.y;

  // Background — calm dark navy.
  float r = length(p);
  vec3 bg = mix(
    vec3(0.05, 0.08, 0.14),
    vec3(0.01, 0.01, 0.04),
    smoothstep(0.0, 0.7, r)
  );

  // Mode frequencies. The ratio w2/w1 = sqrt(1 + 2k/K) where k is
  // the coupling spring constant and K is the wall spring constant.
  // We pick w1 = 1.6 and w2 = 2.4 so the ratio is sqrt(2.25) = 1.5,
  // giving a clean beat frequency of (w2 - w1) / (2π) ≈ 0.13 Hz.
  float w1 = 1.6 + bass * 0.4;
  float w2 = 2.4 + bass * 0.4;
  float A1 = 0.30;
  float A2 = 0.20;
  vec2 d = displacement(uTime, w1, w2, A1, A2);

  // Mass positions on a horizontal track.
  float trackY = 0.10;
  float m1x = -0.30 + d.x * 0.7;
  float m2x = 0.30 + d.y * 0.7;
  vec2 m1 = vec2(m1x, trackY);
  vec2 m2 = vec2(m2x, trackY);

  vec3 col = bg;

  // --- Track baseline ---
  float track = exp(-abs(p.y - trackY) * 80.0) * 0.25;
  col += vec3(0.5, 0.7, 1.0) * track;

  // --- Wall attach points (small glowing dots) ---
  for (int i = 0; i < 2; i++) {
    float wx = (i == 0) ? -0.55 : 0.55;
    float wall = exp(-length(p - vec2(wx, trackY)) * 60.0);
    col += vec3(0.6, 0.8, 1.0) * wall * 0.6;
  }

  // --- Wall springs (zigzag from each wall to the mass) ---
  // 6 zigzags per spring, amplitude modulated by displacement.
  for (int i = 0; i < 6; i++) {
    float fi = float(i);
    float t = (fi + 0.5) / 6.0;
    float wx = -0.55 + (m1x + 0.55) * t;
    float wy = trackY + 0.015 * sin(t * 18.0 + uTime * 4.0);
    float dd = length(p - vec2(wx, wy));
    col += vec3(0.7, 0.9, 1.0) * exp(-dd * 200.0) * 0.5;
  }
  for (int i = 0; i < 6; i++) {
    float fi = float(i);
    float t = (fi + 0.5) / 6.0;
    float wx = m2x + (0.55 - m2x) * t;
    float wy = trackY + 0.015 * sin(t * 18.0 + uTime * 4.0 + 1.5);
    float dd = length(p - vec2(wx, wy));
    col += vec3(0.7, 0.9, 1.0) * exp(-dd * 200.0) * 0.5;
  }

  // --- Coupling spring between masses (helical coil) ---
  float dx = m2x - m1x;
  float helix = 0.012;
  for (int i = 0; i < 40; i++) {
    float fi = float(i);
    float t = fi / 40.0;
    // Centerline between m1 and m2.
    float cx = m1x + dx * t;
    float cy = trackY + 0.025 + 0.025 * sin(t * 16.0 + uTime * 5.0);
    float dd = length(p - vec2(cx, cy));
    col += vec3(0.9, 0.7, 0.3) * exp(-dd * 180.0) * 0.6;
  }

  // --- Masses (bright cores with glow) ---
  for (int i = 0; i < 2; i++) {
    vec2 mpos = (i == 0) ? m1 : m2;
    float dd = length(p - mpos);
    float core = exp(-dd * 250.0);
    float glow = exp(-dd * 30.0);
    col += vec3(1.0, 0.9, 0.6) * core * 1.2;
    col += vec3(0.4, 0.7, 1.0) * glow * 0.5;
  }

  // --- Phase space plot (top-left corner) ---
  // A small inset plot showing the (x1, x2) trajectory. The orbit
  // breathes as energy swaps between modes.
  vec2 phaseOffset = vec2(-0.55, 0.40);
  float phaseSize = 0.18;
  vec2 phaseLocal = (p - phaseOffset) / phaseSize;
  if (max(abs(phaseLocal.x), abs(phaseLocal.y)) < 1.0) {
    // Plot the current point as a bright dot.
    vec2 phaseNow = vec2(d.x, d.y);  // values in [-0.5, 0.5]
    float dot = exp(-length(phaseLocal - phaseNow * 2.0) * 200.0);
    col += vec3(1.0, 0.4, 0.7) * dot * 1.2;
    // Plot the recent trajectory (5 prior points along the curve).
    for (int i = 1; i <= 6; i++) {
      float dt = -float(i) * 0.15;
      vec2 dPast = displacement(uTime + dt, w1, w2, A1, A2);
      vec2 pastPt = vec2(dPast.x, dPast.y) * 2.0;
      float dd = length(phaseLocal - pastPt);
      float alpha = exp(-dd * 80.0) * (1.0 - float(i) / 7.0);
      col += vec3(1.0, 0.5, 0.8) * alpha * 0.5;
    }
    // Box border.
    float box = step(max(abs(phaseLocal.x), abs(phaseLocal.y)), 1.0) -
                step(max(abs(phaseLocal.x), abs(phaseLocal.y)), 0.97);
    col += vec3(0.6, 0.8, 1.0) * box * 0.4;
  }

  // --- Energy density label (visualized as a horizontal bar) ---
  // The beat envelope: |A1|² + A2² + 2·A1·A2·cos((w2-w1)·t).
  // Slow oscillation between "energy in mode 1" (both masses moving
  // together) and "mode 2" (masses opposing).
  float beat = 0.5 + 0.5 * cos((w2 - w1) * uTime);
  // Bar goes from x=-0.55 to x=0.55 at y=0.45.
  float barY = 0.45;
  float barW = 0.04;
  float barX = -0.55 + 1.10 * beat;
  float bar = exp(-length(p - vec2(barX, barY)) * 80.0);
  col += vec3(0.3, 1.0, 0.6) * bar * 0.7;

  // Fade bar background.
  float barBg = exp(-abs(p.y - barY) * 60.0) * 0.08;
  col += vec3(0.5, 0.7, 0.9) * barBg;

  // Tone mapping.
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  // Subtle vignette.
  col *= 1.0 - smoothstep(0.5, 1.0, r) * 0.4;

  outColor = vec4(col, 1.0);
}
`;

export function computeResonanceBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.8 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.1 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.3 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.6 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.9 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.4 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.8 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.3 + 3.0);
}

export function computeResonanceRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createResonancePipeline(gl: WebGL2RenderingContext): ResonancePipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawResonance(
  gl: WebGL2RenderingContext,
  pipe: ResonancePipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
